const settings = {
    templates: {
        result: `<div class="surgeon-locator__result %% CLASSNAME %%" data-id="%% ID %%">
                <div class="surgeon-type">%% TYPE %%</div>
                <div class="content">
                    %% PHOTO %%
                    
                    <h3>%% FULLNAME %%</h3>
                    <p class="type_description">%% DESCRIPTION %%</p>
                    %% LOCATION %%
                    
                    <a target="_blank" class="button" href="%% URL %%">Surgeon profile</a>
                </div>
            </div>`,

        location: `<p>
                <span class="location">%% CITY %%, %% STATE %%</span><br/>
                <span class="practice_name">%% NAME %%</span>
            </p>`,

        pagination: `<div class="surgeon-locator__pagination">
                <div>%% MESSAGE %%</div>
                <div></div>
            </div>`,
    },

    maxResultCount: -1,
    latLngMinDiff: 0.0001,
    resultsPerPage: 10,
    scrollTopPosition: 100,
};

export default class ResultsList {
    constructor(container, callbacks = {}) {
        this.container = container;
        this.callbacks = callbacks;
        this.resultsBySurgeon = [];
        this.resultsByLocation = [];
        this.currentPageIndex = 0;
    }

    setResultsForApiResponse(surgeonResults) {
        // Organize unique locations by surgeon and vice-versa
        this.resultsBySurgeon = [];
        this.resultsByLocation = [];
        for (const surgeonResult of surgeonResults) {
            // Determine if this surgeon's location has already been added to
            // our list of unique locations
            let location = this.resultsByLocation.find((testLocation) => {
                if (
                    Math.abs(testLocation.lat - surgeonResult.lat) >
                    settings.latLngMinDiff
                ) {
                    return;
                }

                if (
                    Math.abs(testLocation.lng - surgeonResult.lng) >
                    settings.latLngMinDiff
                ) {
                    return;
                }

                return true;
            });

            if (location) {
                // This location has already been added to our unique location list -
                // add this surgeon to the preexisting location
                location.surgeons.push(surgeonResult);
            } else {
                // This is a new unique location - add it to our list of unique
                // locations
                location = {
                    lat: surgeonResult.lat,
                    lng: surgeonResult.lng,
                    surgeons: [surgeonResult],
                };

                this.resultsByLocation.push(location);
            }

            // Skip any results for which the surgeon has already been listed
            let uniqueSurgeonResult = this.resultsBySurgeon.find((test) => {
                return test.surgeonID == surgeonResult.surgeonID;
            });

            if (uniqueSurgeonResult) {
                // Add this additional location to our array
                uniqueSurgeonResult.surgeonResults.push(surgeonResult);

                // Stop here
                continue;
            }

            // Add this surgeon ID to our unique ID list
            uniqueSurgeonResult = {
                surgeonID: surgeonResult.surgeonID,
                surgeonResults: [surgeonResult],
            };

            this.resultsBySurgeon.push(uniqueSurgeonResult);

            // Check if we've surpassed our limit
            if (settings.maxResultCount > 0) {
                if (this.resultsBySurgeon.length >= settings.maxResultCount) {
                    break;
                }
            }
        }

        // Show first (or only) page of results
        this.showResultsForPage(0, true);
    }

    showResultsForPage(newPageIndex, showingInitialResults = false) {
        // Make sure the new page index is valid
        const numSurgeons = this.resultsBySurgeon.length;
        const numPages = Math.abs(numSurgeons / settings.resultsPerPage);
        if (newPageIndex < 0 || newPageIndex >= numPages) {
            return;
        }

        // Stop if we're already showing the specified index, unless "showingInitialResults" is set to true
        if (newPageIndex === this.currentPageIndex) {
            if (showingInitialResults !== true) {
                return;
            }
        }

        // Update index
        this.currentPageIndex = newPageIndex;

        // Clear any existing results
        this.container.innerHTML = '';

        // Add results to the DOM
        const startIndex = this.currentPageIndex * settings.resultsPerPage;
        let endIndex = startIndex + settings.resultsPerPage;
        if (endIndex >= numSurgeons) {
            endIndex = numSurgeons;
        }

        for (let i = startIndex; i < endIndex; i++) {
            // Append this surgeon to our results HTML
            const uniqueSurgeonResult = this.resultsBySurgeon[i];
            const practices = uniqueSurgeonResult.surgeonResults
                .map((surgeonResult) => surgeonResult.practice)
                .sort((a, b) => {
                    if (!a.primary && b.primary) {
                        return 1;
                    } else if (a.primary && !b.primary) {
                        return -1;
                    }

                    return 0;
                });

            const element = ResultsList.createResultElement(
                uniqueSurgeonResult.surgeonResults[0],
                practices,
                this.callbacks.resultHoverCallback,
            );

            this.container.appendChild(element);
        }

        // Create pagination elements and add to DOM
        const topPagination = createPaginationElement.call(this);
        this.container.insertBefore(topPagination, this.container.firstChild);

        const bottomPagination = createPaginationElement.call(this);
        this.container.appendChild(bottomPagination);

        // If we're not showing the initial round of results, check that the results are in view
        if (!showingInitialResults) {
            const bounds = this.container.getBoundingClientRect();
            if (bounds.top < settings.scrollTopPosition) {
                window.scrollTo({
                    top:
                        window.pageYOffset +
                        bounds.top -
                        settings.scrollTopPosition,
                    left: 0,
                    behavior: 'smooth',
                });
            }
        }
    }

    static createResultElement(
        surgeonResult,
        practices,
        resultHoverCallback = null,
    ) {
        const fullName = `${surgeonResult.firstname}${
            surgeonResult.middlename ? ' ' + surgeonResult.middlename : ''
        } ${surgeonResult.lastname}`;

        let photo = '';
        if (surgeonResult.photo) {
            photo = `<img src="${surgeonResult.photo}" alt="Photo of ${fullName}" />`;
        }

        let location = '';
        if (practices) {
            // Array of practice locations was provided;
            // Note - practice 2 name is optional
            let previousPracticeName = '';
            for (const practice of practices) {
                let currentPracticeName = practice.name;
                if (!currentPracticeName) {
                    currentPracticeName = previousPracticeName;
                } else {
                    previousPracticeName = currentPracticeName;
                }

                location += settings.templates.location
                    .replace('%% CITY %%', practice.city)
                    .replace('%% STATE %%', practice.state)
                    .replace('%% NAME %%', currentPracticeName);
            }
        } else {
            // Array of practice locations wasn't provided - just use surgeonResult.practice
            location = settings.templates.location
                .replace('%% CITY %%', surgeonResult.practice.city)
                .replace('%% STATE %%', surgeonResult.practice.state)
                .replace('%% NAME %%', surgeonResult.practice.name);
        }

        let surgeonTypeClassName;
        let surgeonTypeTitle;
        let surgeonTypeDescription;
        switch (surgeonResult.specialistType) {
            case 'breast-surgeon':
                surgeonTypeClassName = 'surgeon-type--breast-surgeon';
                surgeonTypeTitle = 'Breast surgeon';
                surgeonTypeDescription =
                    'Performs nerve identification';
                break;

            default: // 'plastic-surgeon'
                surgeonTypeClassName = 'surgeon-type--plastic-surgeon';
                surgeonTypeTitle = 'Plastic surgeon';
                surgeonTypeDescription = 'Performs the Resensation technique';
                break;
        }

        const element = document.createElement('div');
        element.innerHTML = settings.templates.result
            .replace('%% CLASSNAME %%', surgeonTypeClassName)
            .replace('%% ID %%', surgeonResult.surgeonID)
            .replace('%% TYPE %%', surgeonTypeTitle)
            .replace('%% PHOTO %%', photo)
            .replace('%% FULLNAME %%', fullName)
            .replace('%% DESCRIPTION %%', surgeonTypeDescription)
            .replace('%% LOCATION %%', location)
            .replace('%% URL %%', surgeonResult.url);

        // Add hover callback, if provided
        if (resultHoverCallback) {
            // Callback was provided (this is for the main page results)
            const callback = (event) => {
                const hover = event.type == 'mouseenter';
                resultHoverCallback(hover, surgeonResult.surgeonID);
            };

            element.firstChild.addEventListener('mouseenter', callback);
            element.firstChild.addEventListener('mouseleave', callback);
        } else {
            // Callback wasn't provided (this is for the Google map windows) -
            // Ideally this would be more explicit, but we're switching the
            // button class within these because they get focus automatically
            // when opened which looks weird due to the .button class's
            // hover state
            const buttons = element.querySelectorAll('.button');
            for (const button of buttons) {
                button.classList.remove('button');
            }
        }

        return element.firstChild;
    }
}

/*
    Internal functions
*/

/**
 * @this ResultsList
 * @return {Element}
 */
function createPaginationElement() {
    const element = document.createElement('div');

    const numSurgeons = this.resultsBySurgeon.length;
    const numPages = Math.abs(numSurgeons / settings.resultsPerPage);
    const startIndex = this.currentPageIndex * settings.resultsPerPage;
    let endIndex = startIndex + settings.resultsPerPage;
    if (endIndex >= numSurgeons) {
        endIndex = numSurgeons;
    }
    const numPageSurgeons = endIndex - startIndex;

    if (numPages > 1) {
        const message =
            `PAGE ${this.currentPageIndex + 1} | ` +
            `Showing ${numPageSurgeons} closest result${
                numPageSurgeons > 1 ? 's' : ''
            }<br/> ` +
            (numPageSurgeons > 1 ?
                `(results ${startIndex + 1}-${endIndex} of ${numSurgeons})` :
                `(result ${startIndex + 1} of ${numSurgeons})`);

        element.innerHTML = settings.templates.pagination.replace(
            '%% MESSAGE %%',
            message,
        );

        // Setup links
        const linkContainer = element.firstChild.querySelector(
            ':scope > div:last-child',
        );
        for (let i = 0; i < numPages; i++) {
            const pageNumber = i + 1;

            const link = document.createElement('a');
            link.className = i === this.currentPageIndex ? 'page--current' : '';
            link.href = '#';
            link.innerText = pageNumber.toString();

            link.addEventListener('click', (event) => {
                event.preventDefault();
                this.showResultsForPage(i);
            });

            linkContainer.appendChild(link);
        }
    } else {
        const message = `Showing ${numPageSurgeons} closest result${
            numPageSurgeons > 1 ? 's' : ''
        }`;

        element.innerHTML = settings.templates.pagination.replace(
            '%% MESSAGE %%',
            message,
        );
    }

    return element.firstChild;
}
